/** Named looks. Each preset is a flat patch of settings paths. */

export interface Preset {
  name: string;
  blurb: string;
  patch: Record<string, unknown>;
}

/** Shared by every preset that peels a solid block down to the final frame. */
const PEEL_AWAY = {
  'peel.mode': 'consume',
  'peel.slabDepth': 512,
  'peel.traceDepth': 512,
  'peel.fitBox': false,
  'peel.anchor': 'back',
};

export const PRESETS: Preset[] = [
  {
    name: 'Photographic block',
    blurb:
      'A solid cube of footage that strips down to the final frame. The exposed face is the current frame; the sides are the x–t and y–t smear.',
    patch: {
      ...PEEL_AWAY,
      'look.colorMode': 0,
      'look.densityMode': 0,
      'look.density': 8,
      'look.frontOpacity': 0.95,
      'look.depthShade': 0.4,
      'look.stainMix': 0,
      'look.stainGlow': 0,
      'look.exposure': 1,
      'look.tonemap': 2,
      'look.wire': true,
      'look.wireAlpha': 0.2,
      'peel.traceAlpha': 0.07,
      'peel.traceFade': 0,
      'peel.traceStain': 1,
    },
  },
  {
    name: 'Stained block',
    blurb: 'The same solid cube, with every layer tinted by the rule as it is exposed and peeled.',
    patch: {
      ...PEEL_AWAY,
      'look.colorMode': 2,
      'look.densityMode': 0,
      'look.density': 8,
      'look.stainMix': 0.95,
      'look.cutoff': 0.03,
      'look.frontOpacity': 0.45,
      'look.depthShade': 0.35,
      'look.tonemap': 2,
      'rule.key': 'diff',
      'rule.gain': 9,
      'rule.gamma': 0.8,
      'rule.colormap': 0,
      'peel.traceAlpha': 0.14,
      'peel.traceStain': 1,
    },
  },
  {
    name: 'Peel to the trace',
    blurb:
      'Heavier residue: as the block is stripped away the vacated space fills with the stain of everything that already went past.',
    patch: {
      ...PEEL_AWAY,
      'look.colorMode': 0,
      'look.densityMode': 0,
      'look.density': 9,
      'look.frontOpacity': 0.95,
      'look.depthShade': 0.25,
      'look.cutoff': 0.03,
      'look.stainGlow': 0.15,
      'rule.key': 'diff',
      'rule.gain': 8,
      'rule.gamma': 0.9,
      'rule.colormap': 8,
      'peel.traceAlpha': 0.45,
      'peel.traceFade': 0,
      'peel.traceStain': 1,
    },
  },
  {
    name: 'Motion smoke',
    blurb: 'Only what changed has any density, so movement hangs in the cube as a plume.',
    patch: {
      ...PEEL_AWAY,
      'look.colorMode': 1,
      'look.densityMode': 1,
      'look.density': 6,
      'look.cutoff': 0.06,
      'look.frontOpacity': 0,
      'look.depthShade': 0,
      'look.stainGlow': 0.25,
      'look.exposure': 1.15,
      'look.tonemap': 2,
      'look.background': '#04040a',
      'rule.key': 'diff',
      'rule.gain': 6,
      'rule.gamma': 1.4,
      'rule.blur': 1,
      'rule.colormap': 1,
      'peel.traceAlpha': 0.5,
      'peel.traceStain': 1,
    },
  },
  {
    name: 'Fire & ice',
    blurb: 'Signed luminance change: warm where the frame brightened, cool where it darkened.',
    patch: {
      ...PEEL_AWAY,
      'look.colorMode': 1,
      'look.densityMode': 1,
      'look.density': 7,
      'look.cutoff': 0.05,
      'look.frontOpacity': 0,
      'look.stainGlow': 0.3,
      'look.exposure': 1.2,
      'look.background': '#03030a',
      'rule.key': 'signed',
      'rule.gain': 7,
      'rule.gamma': 1.2,
      'rule.blur': 1,
      'rule.colormap': 6,
      'peel.traceAlpha': 0.5,
      'peel.traceStain': 1,
    },
  },
  {
    name: 'Comet trails',
    blurb: 'Decaying peak-hold, so anything that moves drags a tail back through time.',
    patch: {
      ...PEEL_AWAY,
      'look.colorMode': 1,
      'look.densityMode': 1,
      'look.density': 5,
      'look.cutoff': 0.08,
      'look.frontOpacity': 0.25,
      'look.stainGlow': 0.2,
      'rule.key': 'persist',
      'rule.gain': 3.5,
      'rule.decay': 0.95,
      'rule.colormap': 1,
      'peel.traceAlpha': 0.4,
      'peel.traceStain': 1,
    },
  },
  {
    name: 'Still-life X-ray',
    blurb: 'Stains what held still and dissolves the movers — the opposite of a motion pass.',
    patch: {
      ...PEEL_AWAY,
      'look.colorMode': 2,
      'look.densityMode': 2,
      'look.density': 3.2,
      'look.cutoff': 0.25,
      'look.frontOpacity': 0,
      'look.stainMix': 0.5,
      'look.exposure': 1.1,
      'rule.key': 'similarity',
      'rule.gain': 7,
      'rule.gamma': 2.2,
      'rule.colormap': 9,
      'peel.traceAlpha': 0.3,
      'peel.traceStain': 1,
    },
  },
  {
    name: 'Flow ribbons',
    blurb: 'Hue encodes the direction of travel, so paths through the block read as colored ribbons.',
    patch: {
      ...PEEL_AWAY,
      'look.colorMode': 1,
      'look.densityMode': 1,
      'look.density': 6,
      'look.cutoff': 0.05,
      'look.frontOpacity': 0,
      'look.stainGlow': 0.35,
      'look.exposure': 1.1,
      'rule.key': 'flow',
      'rule.gain': 3,
      'rule.blur': 1,
      'rule.colormap': 7,
      'peel.traceAlpha': 0.5,
      'peel.traceStain': 1,
    },
  },
  {
    name: 'Sliding slab',
    blurb: 'A thin travelling window of time instead of the whole block — a shutter moving through t.',
    patch: {
      'look.colorMode': 2,
      'look.densityMode': 0,
      'look.density': 8,
      'look.frontOpacity': 0.9,
      'look.stainMix': 0.7,
      'rule.key': 'diff',
      'rule.gain': 5,
      'rule.colormap': 0,
      'peel.mode': 'consume',
      'peel.slabDepth': 24,
      'peel.traceDepth': 40,
      'peel.traceAlpha': 0.2,
      'peel.traceFade': 0.03,
      'peel.traceStain': 1,
      'peel.fitBox': false,
      'peel.anchor': 'back',
    },
  },
  {
    name: 'Building up',
    blurb: 'The reverse: an empty box that fills in behind the playhead, one layer at a time.',
    patch: {
      'look.colorMode': 0,
      'look.densityMode': 0,
      'look.density': 8,
      'look.frontOpacity': 0.95,
      'look.depthShade': 0.35,
      'rule.key': 'diff',
      'rule.gain': 5,
      'peel.mode': 'accumulate',
      'peel.slabDepth': 512,
      'peel.traceDepth': 0,
      'peel.traceAlpha': 0,
      'peel.fitBox': true,
      'peel.anchor': 'front',
    },
  },
];

export function presetByName(name: string): Preset | undefined {
  return PRESETS.find((p) => p.name === name);
}
